// Tek bir PDF sayfası + üzerindeki çizim katmanları + kalem/fare/dokunma girişi.
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import type { Annotation, FigureInput, InkAnnotation, InkTool, Participant, Point, ShapeInput, ShapeKind, TextInput } from '../../shared/protocol';
import { LIMITS } from '../../shared/protocol';
import { ANNOTATION_FONT, TEXT_LINE_HEIGHT, hitTest } from '../../shared/geometry';
import { randomId } from '../../shared/ids';
import { PageRenderer, effectiveDpr, type LocalLive, type RemoteLive } from '../canvas/renderer';
import type { ShapeDraw } from '../canvas/draw';
import type { LessonConnection } from '../lib/connection';
import type { History } from '../lib/history';
import { lesson, lessonStore } from '../lib/lesson';
import type { PDFDocumentProxy, RenderTask } from '../lib/pdf';
import { useStore } from '../lib/store';
import { SIZES, mathStore, setTool, toolStore, type Tool } from '../lib/tools';
import { clamp } from '../lib/util';

const r2 = (n: number) => Math.round(n * 100) / 100;
/** Bazı tarayıcılar (ve sentetik olaylar) yakalamada hata fırlatır; çizimi engellememeli. */
const capture = (el: Element, pointerId: number) => {
  try {
    el.setPointerCapture(pointerId);
  } catch {
    // yok say
  }
};
const r3 = (n: number) => Math.round(n * 1000) / 1000;
const EMPTY: Annotation[] = [];

export type FitMode = 'width' | 'page';

interface Shown {
  page: number;
  w: number;
  h: number;
  scale: number;
}

interface Active {
  pointerId: number;
  kind: 'ink' | 'shape' | 'erase' | 'laser' | 'scroll';
  lastX: number;
  lastY: number;
  lastPage?: { x: number; y: number };
}

interface TextEdit {
  page: number;
  x: number;
  y: number;
  value: string;
}

export interface PageViewProps {
  doc: PDFDocumentProxy;
  page: number;
  fit: FitMode;
  zoom: number;
  viewportWidth: number;
  viewportHeight: number;
  scrollEl: HTMLElement | null;
  conn: LessonConnection;
  history: History;
  self: Participant;
  canDraw: boolean;
  isTeacher: boolean;
  onShown?: (page: number) => void;
}

function remoteFor(page: number): RemoteLive {
  const s = lessonStore.get();
  const drafts: InkAnnotation[] = [];
  for (const id in s.drafts) if (s.drafts[id].page === page) drafts.push(s.drafts[id]);
  const previews: ShapeDraw[] = [];
  for (const id in s.previews) if (s.previews[id].page === page) previews.push(s.previews[id]);
  const lasers = Object.values(s.lasers).filter((l) => l.page === page);
  return { drafts, previews, lasers };
}

export function PageView(props: PageViewProps) {
  const { doc, page, fit, zoom, viewportWidth, viewportHeight, conn, history, self, canDraw, isTeacher } = props;
  const tool = useStore(toolStore, (s) => s.tool);
  const textColor = useStore(toolStore, (s) => s.color.text);
  const textSize = useStore(toolStore, (s) => SIZES.text[s.size.text]);

  const wrapRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<HTMLCanvasElement>(null);
  const hlRef = useRef<HTMLCanvasElement>(null);
  const inkRef = useRef<HTMLCanvasElement>(null);
  const liveRef = useRef<HTMLCanvasElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const rendererRef = useRef<PageRenderer | null>(null);
  const syncRef = useRef<() => void>(() => {});

  const [shown, setShown] = useState<Shown | null>(null);
  const shownRef = useRef<Shown | null>(null);
  const [textEdit, setTextEdit] = useState<TextEdit | null>(null);
  const textRef = useRef<TextEdit | null>(null);
  textRef.current = textEdit;

  const local = useRef<LocalLive>({ ink: null, shape: null, eraser: null, figure: null });
  const inkMeta = useRef<{ id: string; page: number; pending: Point[]; timer: number } | null>(null);
  const active = useRef<Active | null>(null);
  const erased = useRef<Annotation[]>([]);
  const penSeen = useRef(false);
  const shapePage = useRef(0);
  const lastSent = useRef({ preview: 0, laser: 0 });

  // ------------------------------------------------------------ çizim motoru
  useEffect(() => {
    const renderer = new PageRenderer(
      { hl: hlRef.current!, ink: inkRef.current!, live: liveRef.current! },
      () => local.current,
      remoteFor,
    );
    rendererRef.current = renderer;
    const last: { page?: number; list?: readonly Annotation[]; drafts?: object; previews?: object; lasers?: object } = {};
    const sync = () => {
      const p = renderer.page;
      if (!p) return;
      const s = lessonStore.get();
      const list = s.pages[p] ?? EMPTY;
      if (list !== last.list || p !== last.page) {
        last.list = list;
        last.page = p;
        renderer.setAnnotations(list);
      }
      if (s.drafts !== last.drafts) {
        last.drafts = s.drafts;
        renderer.invalidateLive(true);
      }
      if (s.previews !== last.previews || s.lasers !== last.lasers) {
        last.previews = s.previews;
        last.lasers = s.lasers;
        renderer.invalidateLive();
      }
    };
    syncRef.current = sync;
    const unsubscribe = lessonStore.subscribe(sync);
    let alive = true;
    document.fonts
      ?.load(`20px "${ANNOTATION_FONT}"`)
      .then(() => alive && renderer.repaintAll())
      .catch(() => {});
    return () => {
      alive = false;
      unsubscribe();
      renderer.destroy();
      rendererRef.current = null;
    };
  }, []);

  // ------------------------------------------------------------ PDF sayfası
  useEffect(() => {
    if (viewportWidth <= 0) return;
    let cancelled = false;
    let task: RenderTask | null = null;
    (async () => {
      const pg = await doc.getPage(page);
      if (cancelled) return;
      const base = pg.getViewport({ scale: 1 });
      const availW = Math.max(240, viewportWidth - 40);
      const availH = Math.max(240, viewportHeight - 40);
      const fitScale = fit === 'page' ? Math.min(availW / base.width, availH / base.height) : availW / base.width;
      const scale = clamp(fitScale * zoom, 0.2, 8);
      const dpr = effectiveDpr(base.width * scale, base.height * scale);
      const vp = pg.getViewport({ scale: scale * dpr });
      // Önce görünmeyen tuvale çiz, bitince değiştir: sayfa çevirirken beyaz yanıp sönme olmaz.
      const off = document.createElement('canvas');
      off.width = Math.max(1, Math.round(vp.width));
      off.height = Math.max(1, Math.round(vp.height));
      task = pg.render({ canvas: off, viewport: vp });
      try {
        await task.promise;
      } catch (err) {
        if (!cancelled) console.warn('PDF sayfası çizilemedi', err);
        return;
      }
      if (cancelled) return;
      const visible = pdfRef.current!;
      visible.width = off.width;
      visible.height = off.height;
      visible.getContext('2d')!.drawImage(off, 0, 0);
      const next = { page, w: base.width, h: base.height, scale };
      rendererRef.current?.configure(page, base.width, base.height, scale, dpr);
      shownRef.current = next;
      setShown(next);
      syncRef.current();
      props.onShown?.(page);
    })();
    return () => {
      cancelled = true;
      task?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, page, fit, zoom, viewportWidth, viewportHeight]);

  // Gösterilen sayfa değişince yarım kalan çizimi o sayfada bitir, açık metni kaydet.
  useEffect(() => {
    if (!shown) return;
    if (inkMeta.current && inkMeta.current.page !== shown.page) {
      active.current = null;
      endInk(true);
    }
    if (local.current.shape && shapePage.current !== shown.page) {
      active.current = null;
      endShape();
    }
    if (textRef.current && textRef.current.page !== shown.page) commitText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown?.page]);

  // İzin kalkarsa süren çizimi bırak (sunucu yarım izi zaten kaydedip herkese gönderir).
  useEffect(() => {
    if (canDraw) return;
    if (inkMeta.current) endInk(false);
    if (local.current.shape) {
      local.current.shape = null;
      conn.send({ t: 'preview', p: null });
    }
    local.current.eraser = null;
    active.current = null;
    textRef.current = null;
    setTextEdit(null);
    rendererRef.current?.invalidateLive(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canDraw]);

  // Araç değişince silgi imlecini kaldır, açık metni kaydet.
  useEffect(() => {
    if (tool !== 'eraser' && local.current.eraser) {
      local.current.eraser = null;
      rendererRef.current?.invalidateLive();
    }
    if (tool !== 'text' && textRef.current) commitText();
    if (tool !== 'figure') {
      if (local.current.figure) {
        local.current.figure = null;
        rendererRef.current?.invalidateLive();
      }
      if (mathStore.get().figure) mathStore.set({ figure: null });
    }
    if (tool !== 'text' && mathStore.get().pendingSymbol) mathStore.set({ pendingSymbol: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool]);

  // Yeni metin kutusuna odaklan (fare olaylarının varsayılan davranışlarından sonra).
  useEffect(() => {
    if (!textEdit) return;
    const id = requestAnimationFrame(() => {
      const el = textAreaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
    return () => cancelAnimationFrame(id);
  }, [textEdit?.x, textEdit?.y, textEdit !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Matematik paneli, metin kutusu açıkken işareti imlecin olduğu yere yazar.
  const editing = textEdit !== null;
  useEffect(() => {
    if (!editing) return;
    const insert = (text: string) => {
      const el = textAreaRef.current;
      const cur = textRef.current;
      if (!el || !cur) return;
      const start = el.selectionStart;
      const value = cur.value.slice(0, start) + text + cur.value.slice(el.selectionEnd);
      if (value.length > LIMITS.maxTextLength) return;
      const next = { ...cur, value };
      textRef.current = next;
      setTextEdit(next);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + text.length, start + text.length);
      });
    };
    mathStore.set({ insert });
    return () => mathStore.set({ insert: null });
  }, [editing]);

  // ------------------------------------------------------------ yardımcılar
  const toPage = (e: { clientX: number; clientY: number }) => {
    const r = wrapRef.current!.getBoundingClientRect();
    const sc = shownRef.current?.scale ?? 1;
    return { x: (e.clientX - r.left) / sc, y: (e.clientY - r.top) / sc };
  };
  const pressureOf = (e: PointerEvent) => (e.pointerType === 'pen' ? clamp(e.pressure || 0.5, 0.05, 1) : 0.5);
  const drawingAllowed = (t: Tool) => (t === 'laser' ? isTeacher : t !== 'pan' && canDraw);
  const eraserRadius = () => SIZES.eraser[toolStore.get().size.eraser];
  const author = () => ({ authorId: self.id, authorName: self.name, authorRole: self.role, createdAt: Date.now() });

  // ------------------------------------------------------------ kalem
  function beginInk(inkTool: InkTool, p: { x: number; y: number }, pressure: number) {
    const sh = shownRef.current!;
    const ts = toolStore.get();
    const color = inkTool === 'highlighter' ? ts.color.highlighter : ts.color.pen;
    const size = SIZES[inkTool][ts.size[inkTool]];
    const pt: Point = [r2(p.x), r2(p.y), r3(pressure)];
    const id = randomId();
    if (!conn.send({ t: 'ink:start', id, page: sh.page, tool: inkTool, color, size, point: pt })) return false;
    local.current.ink = { tool: inkTool, color, size, points: [pt] };
    // Noktalar ~30 kez/sn paketlenip gönderilir; karşı taraf izi canlı görür.
    inkMeta.current = { id, page: sh.page, pending: [], timer: window.setInterval(flushInk, 33) };
    rendererRef.current?.invalidateLive(inkTool === 'highlighter');
    return true;
  }

  function extendInk(p: { x: number; y: number }, pressure: number) {
    const ink = local.current.ink;
    const meta = inkMeta.current;
    if (!ink || !meta) return;
    const last = ink.points[ink.points.length - 1];
    if (Math.abs(p.x - last[0]) < 0.35 && Math.abs(p.y - last[1]) < 0.35) return;
    if (ink.points.length >= LIMITS.maxPointsPerStroke) return;
    const pt: Point = [r2(p.x), r2(p.y), r3(pressure)];
    ink.points.push(pt);
    meta.pending.push(pt);
  }

  function flushInk() {
    const meta = inkMeta.current;
    if (!meta || !meta.pending.length) return;
    const pts = meta.pending.splice(0);
    for (let i = 0; i < pts.length; i += LIMITS.maxPointsPerAppend) {
      conn.send({ t: 'ink:append', id: meta.id, points: pts.slice(i, i + LIMITS.maxPointsPerAppend) });
    }
  }

  function endInk(commit: boolean) {
    const ink = local.current.ink;
    const meta = inkMeta.current;
    if (!ink || !meta) return;
    clearInterval(meta.timer);
    if (commit) flushInk(); // kalan noktaları gönder (inkMeta henüz dolu)
    local.current.ink = null;
    inkMeta.current = null;
    if (commit) {
      conn.send({ t: 'ink:end', id: meta.id });
      const ann: InkAnnotation = { kind: 'ink', id: meta.id, page: meta.page, tool: ink.tool, color: ink.color, size: ink.size, points: ink.points, ...author() };
      lesson.add([ann]);
      history.push({ kind: 'add', anns: [ann] });
    }
    rendererRef.current?.invalidateLive(ink.tool === 'highlighter');
  }

  // ------------------------------------------------------------ şekiller
  function beginShape(kind: ShapeKind, p: { x: number; y: number }) {
    const ts = toolStore.get();
    shapePage.current = shownRef.current!.page;
    local.current.shape = { shape: kind, color: ts.color.shape, size: SIZES.shape[ts.size.shape], x1: r2(p.x), y1: r2(p.y), x2: r2(p.x), y2: r2(p.y) };
    rendererRef.current?.invalidateLive();
  }

  function updateShape(p: { x: number; y: number }, constrain: boolean) {
    const s = local.current.shape;
    if (!s) return;
    let x2 = p.x;
    let y2 = p.y;
    if (constrain) {
      const dx = x2 - s.x1;
      const dy = y2 - s.y1;
      if (s.shape !== 'line' && s.shape !== 'arrow') {
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        x2 = s.x1 + Math.sign(dx || 1) * d;
        y2 = s.y1 + Math.sign(dy || 1) * d;
      } else {
        const step = Math.PI / 12; // 15°
        const ang = Math.round(Math.atan2(dy, dx) / step) * step;
        const len = Math.hypot(dx, dy);
        x2 = s.x1 + Math.cos(ang) * len;
        y2 = s.y1 + Math.sin(ang) * len;
      }
    }
    s.x2 = r2(x2);
    s.y2 = r2(y2);
    rendererRef.current?.invalidateLive();
    const now = performance.now();
    if (now - lastSent.current.preview > 40) {
      lastSent.current.preview = now;
      conn.send({ t: 'preview', p: { page: shapePage.current, ...s } });
    }
  }

  function endShape() {
    const s = local.current.shape;
    local.current.shape = null;
    rendererRef.current?.invalidateLive();
    conn.send({ t: 'preview', p: null });
    if (!s || Math.hypot(s.x2 - s.x1, s.y2 - s.y1) < 2) return;
    const input: ShapeInput = { kind: 'shape', id: randomId(), page: shapePage.current, ...s };
    if (!conn.send({ t: 'ann:add', a: input })) return;
    const ann: Annotation = { ...input, ...author() };
    lesson.add([ann]);
    history.push({ kind: 'add', anns: [ann] });
  }

  // ------------------------------------------------------------ silgi
  function eraseAlong(from: { x: number; y: number } | undefined, to: { x: number; y: number }) {
    const sh = shownRef.current;
    if (!sh) return;
    const r = eraserRadius();
    const samples: { x: number; y: number }[] = [];
    if (from) {
      const dist = Math.hypot(to.x - from.x, to.y - from.y);
      const n = Math.min(50, Math.ceil(dist / (r * 0.5)));
      for (let i = 1; i < n; i++) samples.push({ x: from.x + ((to.x - from.x) * i) / n, y: from.y + ((to.y - from.y) * i) / n });
    }
    samples.push(to);
    const list = lessonStore.get().pages[sh.page] ?? EMPTY;
    const hits = list.filter((a) => (isTeacher || a.authorId === self.id) && samples.some((q) => hitTest(a, q.x, q.y, r)));
    if (!hits.length) return;
    const ids = hits.map((a) => a.id);
    if (!conn.send({ t: 'ann:delete', ids })) return;
    lesson.remove(ids);
    erased.current.push(...hits);
  }

  function endErase() {
    if (erased.current.length) history.push({ kind: 'remove', anns: erased.current });
    erased.current = [];
  }

  // ------------------------------------------------------------ lazer
  function sendLaser(p: { x: number; y: number } | null, force: boolean) {
    const sh = shownRef.current;
    if (!sh) return;
    if (p === null) {
      conn.send({ t: 'laser', p: null });
      lesson.laser(self.id, null);
      return;
    }
    const now = performance.now();
    if (!force && now - lastSent.current.laser < 33) return;
    lastSent.current.laser = now;
    const lp = { page: sh.page, x: r2(p.x), y: r2(p.y) };
    conn.send({ t: 'laser', p: lp });
    lesson.laser(self.id, lp); // öğretmen kendi lazerini de görsün
  }

  // ------------------------------------------------------------ metin
  function openText(p: { x: number; y: number }) {
    const sh = shownRef.current!;
    const next = { page: sh.page, x: r2(p.x), y: r2(p.y), value: mathStore.get().pendingSymbol ?? '' };
    mathStore.set({ pendingSymbol: null });
    textRef.current = next;
    setTextEdit(next);
  }

  // ------------------------------------------------------------ formül ve çizimler
  /** Sayfanın dışına taşmayacak sol üst köşe. */
  function figureOrigin(p: { x: number; y: number }, w: number, h: number) {
    const sh = shownRef.current!;
    return { x: r2(clamp(p.x, 0, Math.max(0, sh.w - w))), y: r2(clamp(p.y, 0, Math.max(0, sh.h - h))) };
  }

  function previewFigure(p: { x: number; y: number }) {
    const f = mathStore.get().figure;
    if (!f) return;
    local.current.figure = { ...figureOrigin(p, f.w, f.h), texts: f.texts, lines: f.lines, color: toolStore.get().color.shape };
    rendererRef.current?.invalidateLive();
  }

  function placeFigure(p: { x: number; y: number }) {
    const f = mathStore.get().figure;
    if (!f) return;
    const input: FigureInput = { kind: 'figure', id: randomId(), page: shownRef.current!.page, color: toolStore.get().color.shape, ...figureOrigin(p, f.w, f.h), name: f.name, w: f.w, h: f.h, texts: f.texts, lines: f.lines };
    if (!conn.send({ t: 'ann:add', a: input })) return;
    const ann: Annotation = { ...input, ...author() };
    lesson.add([ann]);
    history.push({ kind: 'add', anns: [ann] });
    local.current.figure = null;
    mathStore.set({ figure: null });
    rendererRef.current?.invalidateLive();
    setTool('pen');
  }

  function commitText() {
    const t = textRef.current;
    if (!t) return;
    textRef.current = null;
    setTextEdit(null);
    const value = t.value.replace(/\s+$/, '');
    if (!value.trim()) return;
    const ts = toolStore.get();
    const input: TextInput = {
      kind: 'text',
      id: randomId(),
      page: t.page,
      x: t.x,
      y: t.y,
      text: value.slice(0, LIMITS.maxTextLength),
      color: ts.color.text,
      fontSize: SIZES.text[ts.size.text],
    };
    if (!conn.send({ t: 'ann:add', a: input })) return;
    const ann: Annotation = { ...input, ...author() };
    lesson.add([ann]);
    history.push({ kind: 'add', anns: [ann] });
  }

  // ------------------------------------------------------------ giriş olayları
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!shownRef.current || active.current) return; // ikinci parmak/avuç içi yok sayılır
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.pointerType === 'pen') penSeen.current = true;
    if (textRef.current) {
      commitText();
      if (tool !== 'text') return;
    }
    const allowed = drawingAllowed(tool);
    // Kalem kullanıldıysa parmak çizmez, sayfayı kaydırır (avuç içi koruması).
    const draw = allowed && !(e.pointerType === 'touch' && penSeen.current);
    if (!draw) {
      if (e.pointerType !== 'touch' || allowed) {
        active.current = { pointerId: e.pointerId, kind: 'scroll', lastX: e.clientX, lastY: e.clientY };
        capture(e.currentTarget, e.pointerId);
      }
      return;
    }
    e.preventDefault();
    capture(e.currentTarget, e.pointerId);
    const p = toPage(e);
    const base = { pointerId: e.pointerId, lastX: e.clientX, lastY: e.clientY, lastPage: p };
    switch (tool) {
      case 'pen':
      case 'highlighter':
        if (beginInk(tool, p, pressureOf(e.nativeEvent))) active.current = { ...base, kind: 'ink' };
        break;
      case 'eraser':
        erased.current = [];
        local.current.eraser = { x: p.x, y: p.y, r: eraserRadius() };
        eraseAlong(undefined, p);
        rendererRef.current?.invalidateLive();
        active.current = { ...base, kind: 'erase' };
        break;
      case 'line':
      case 'arrow':
      case 'rect':
      case 'ellipse':
      case 'triangle':
      case 'rightTriangle':
      case 'trapezoid':
      case 'parallelogram':
      case 'rhombus':
      case 'pentagon':
      case 'hexagon':
      case 'octagon':
        beginShape(tool, p);
        active.current = { ...base, kind: 'shape' };
        break;
      case 'text':
        openText(p);
        break;
      case 'figure':
        placeFigure(p);
        break;
      case 'laser':
        sendLaser(p, true);
        active.current = { ...base, kind: 'laser' };
        break;
      case 'pan':
        break;
    }
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!shownRef.current) return;
    const a = active.current;
    if (!a) {
      if (e.pointerType !== 'touch') {
        if (tool === 'eraser' && canDraw) {
          const p = toPage(e);
          local.current.eraser = { x: p.x, y: p.y, r: eraserRadius() };
          rendererRef.current?.invalidateLive();
        } else if (tool === 'figure' && canDraw) {
          previewFigure(toPage(e));
        } else if (tool === 'laser' && isTeacher && e.pointerType === 'mouse') {
          sendLaser(toPage(e), false);
        }
      }
      return;
    }
    if (a.pointerId !== e.pointerId) return;
    switch (a.kind) {
      case 'ink': {
        const coalesced = e.nativeEvent.getCoalescedEvents?.() ?? [];
        for (const ev of coalesced.length ? coalesced : [e.nativeEvent]) extendInk(toPage(ev), pressureOf(ev));
        rendererRef.current?.invalidateLive(local.current.ink?.tool === 'highlighter');
        break;
      }
      case 'shape':
        updateShape(toPage(e), e.shiftKey);
        break;
      case 'erase': {
        const p = toPage(e);
        local.current.eraser = { x: p.x, y: p.y, r: eraserRadius() };
        eraseAlong(a.lastPage, p);
        a.lastPage = p;
        rendererRef.current?.invalidateLive();
        break;
      }
      case 'laser':
        sendLaser(toPage(e), false);
        break;
      case 'scroll': {
        const el = props.scrollEl;
        if (el) {
          el.scrollLeft -= e.clientX - a.lastX;
          el.scrollTop -= e.clientY - a.lastY;
        }
        a.lastX = e.clientX;
        a.lastY = e.clientY;
        break;
      }
    }
  };

  const onPointerEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    const a = active.current;
    if (!a || a.pointerId !== e.pointerId) return;
    active.current = null;
    switch (a.kind) {
      case 'ink':
        endInk(true);
        break;
      case 'shape':
        endShape();
        break;
      case 'erase':
        endErase();
        if (e.pointerType !== 'mouse') {
          local.current.eraser = null;
          rendererRef.current?.invalidateLive();
        }
        break;
      case 'laser':
        if (e.pointerType !== 'mouse') sendLaser(null, true);
        break;
      case 'scroll':
        break;
    }
  };

  const onPointerLeave = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || active.current) return;
    if (local.current.eraser || local.current.figure) {
      local.current.eraser = null;
      local.current.figure = null;
      rendererRef.current?.invalidateLive();
    }
    if (tool === 'laser' && isTeacher) sendLaser(null, true);
  };

  const onTextKey = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      textRef.current = null;
      setTextEdit(null);
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commitText();
    }
  };

  const allowed = drawingAllowed(tool);
  const cursor = !allowed
    ? tool === 'pan'
      ? 'grab'
      : 'default'
    : tool === 'eraser' || tool === 'laser'
      ? 'none'
      : tool === 'text'
        ? 'text'
        : 'crosshair';
  const scale = shown?.scale ?? 1;
  const lines = textEdit ? textEdit.value.split('\n') : [];

  return (
    <div className="page-outer">
      <div
        ref={wrapRef}
        className="page-wrap"
        data-page={shown?.page ?? 0}
        style={{
          width: shown ? shown.w * shown.scale : 0,
          height: shown ? shown.h * shown.scale : 0,
          visibility: shown ? 'visible' : 'hidden',
          cursor,
          touchAction: allowed ? 'none' : 'pan-x pan-y',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onPointerLeave={onPointerLeave}
        onMouseDown={(e) => {
          if (tool === 'text' && allowed) e.preventDefault();
        }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <canvas ref={pdfRef} className="layer layer-pdf" />
        <canvas ref={hlRef} className="layer layer-hl" />
        <canvas ref={inkRef} className="layer layer-ink" />
        <canvas ref={liveRef} className="layer layer-live" />
        {textEdit && shown && textEdit.page === shown.page && (
          <textarea
            ref={textAreaRef}
            className="text-editor"
            value={textEdit.value}
            spellCheck={false}
            placeholder="Yazın…"
            rows={Math.max(1, lines.length)}
            cols={Math.max(8, ...lines.map((l) => l.length + 2))}
            style={{
              left: textEdit.x * scale,
              top: textEdit.y * scale,
              fontSize: textSize * scale,
              lineHeight: TEXT_LINE_HEIGHT,
              color: textColor,
            }}
            onChange={(e) => {
              const next = { ...textEdit, value: e.target.value };
              textRef.current = next;
              setTextEdit(next);
            }}
            onKeyDown={onTextKey}
            onBlur={commitText}
            onPointerDown={(e) => e.stopPropagation()}
          />
        )}
      </div>
    </div>
  );
}
